import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  Col,
  ListGroup,
  ListGroupItem,
  Row
} from 'reactstrap';
import RouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import { modelOf } from '../../../../prop-types';
import Balance from '../Balance';
import globalTranslations from '../../../../i18n/globalTranslations';
import Paths from '../../../../types/Paths';
import generatePath from '../../../../util/generatePath';
import RouteService from '../../../../services/RouteService';
import UIStore from '../../../../store/UIStore';
import BalanceForm from '../../../user/BalanceForm';
import CustomerUserStore from '../../../../store/CustomerUserStore';

const MAX_BALANCE = 99999;
const INCREMENT_STEP = 2;
const QUANTITY_STEP = 0;
@observer
export class BalanceUserList extends Component {
  handlePaymentClick = () => {
    this.props.onPayment();
  };

  onBalanceUpdate = (id, value, user) => {
    this.props.onBalanceUpdate(id, value, user);
  };

  onBalanceError = (id, quantityField) => {
    this.props.onBalanceError(id, quantityField);
  };

  editUser = (id) => {
    const { routeService, history } = this.props;
    const path = routeService.getPath(
      generatePath(Paths.MyAccountUser, { id })
    );
    return history.push(path);
  };

  removeUser = (id) => {
    this.props.onRemoveUser(id);
  };

  renderBalanceUsers = () => {
    const { customerUserStore, uiStore, showBalance } = this.props;
    const { balanceUserArray } = customerUserStore;
    return (
      balanceUserArray &&
      balanceUserArray.length > 0 && (
        <ListGroup>
          {!uiStore.isSmallest && this.renderListGroupHeaders(showBalance)}
          {balanceUserArray.map((user, i) => (
            <ListGroupItem key={i}>
              <Row className="BalanceUserList__user-details">
                <Col
                  xs="12"
                  sm="6"
                  md="3"
                  className="BalanceUserList__user-name"
                >
                  {user.firstname} {user.lastname}
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="3"
                  className="BalanceUserList__user-balance"
                >
                  {this.renderBalanceUserBalance(user)}
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md={showBalance ? '4' : '3'}
                  className={
                    showBalance
                      ? 'BalanceUserList__user-actions-button-group'
                      : 'BalanceUserList__user-account-status'
                  }
                >
                  {showBalance
                    ? this.renderBalanceUserBalanceForm(user)
                    : this.getAccountStatus(user)}
                </Col>
                {this.renderUserActionButtonGroupForSmallAndUpDevices(
                  user,
                  showBalance
                )}
              </Row>
            </ListGroupItem>
          ))}
        </ListGroup>
      )
    );
  };

  renderListGroupHeaders = (showBalance) => {
    return (
      <ListGroupItem className="BalanceUserList__list-headers">
        <Row>
          <Col sm="6" md="3">
            <FormattedMessage {...globalTranslations.balanceUserNameTitle} />
          </Col>
          <Col sm="6" md="3">
            <FormattedMessage
              {...globalTranslations.balanceUserCurrentBalance}
            />
          </Col>
          <Col sm="6" md={showBalance ? '4' : '3'}>
            <FormattedMessage
              {...(showBalance
                ? globalTranslations.balanceUserNewBalance
                : globalTranslations.balanceUserAccountStatus)}
            />
          </Col>
          {showBalance && (
            <Col sm="6" md="2">
              <FormattedMessage
                {...globalTranslations.balanceUserPaymentTotal}
              />
            </Col>
          )}
        </Row>
      </ListGroupItem>
    );
  };

  renderBalanceUserBalance = (user) => (
    <Balance
      amount={user.account_balance}
      currencyType={user.account_currency}
    />
  );

  renderBalanceUserBalanceForm = (user) => (
    <BalanceForm
      user={user}
      onSubmit={this.handlePaymentClick}
      incrementStep={INCREMENT_STEP}
      quantityStep={QUANTITY_STEP}
      maxQuantity={MAX_BALANCE}
      onBalanceUpdate={this.onBalanceUpdate}
      onBalanceError={this.onBalanceError}
    />
  );

  renderUserActionButtonGroup = (user) => (
    <ButtonGroup
      className="BalanceUserList__user-actions-button-group"
      vertical={this.props.uiStore.isTablet || this.props.uiStore.isHD}
    >
      <Button
        className="BalanceUserList__user-actions-button-add-user"
        onClick={() => this.editUser(user.id)}
        size="lg"
      >
        <FormattedMessage {...globalTranslations.editTitle} />
      </Button>
      <Button
        className="BalanceUserList__user-actions-button-delete-user"
        onClick={() =>
          window.confirm(
            this.props.intl.formatMessage(globalTranslations.confirmRemoveTitle)
          ) && this.removeUser(user.id)
        }
        color="danger"
        size="lg"
      >
        <FormattedMessage {...globalTranslations.removeTitle} />
      </Button>
    </ButtonGroup>
  );

  getAccountStatus = (user) => {
    return user.is_active ? (
      <FormattedMessage
        id="myAccount.balanceUserAccountStatusActive"
        defaultMessage="Active"
      />
    ) : (
      <FormattedMessage
        id="myAccount.balanceUserAccountStatusNotActive"
        defaultMessage="Disabled"
      />
    );
  };

  renderUserActionButtonGroupForSmallAndUpDevices = (user, showBalance) => {
    const totalSum = this.findUserTotal(user);
    const cssClass = showBalance
      ? 'BalanceUserList__user-total-balance'
      : 'BalanceUserList__user-actions';

    return (
      <Col xs="12" sm="6" md={showBalance ? '2' : '3'} className={cssClass}>
        {showBalance
          ? this.renderBalance(user, totalSum)
          : this.renderUserActionButtonGroup(user)}
      </Col>
    );
  };

  findUserTotal = (user) => {
    const { totalSums } = this.props;
    return (
      totalSums.length > 0 &&
      totalSums.find((totalSum) => totalSum.id === user.id)
    );
  };

  renderBalance = (user, totalSum) =>
    totalSum ? (
      <Balance amount={totalSum.sum} currencyType={user.account_currency} />
    ) : (
      <Balance amount={0} currencyType={user.account_currency} />
    );

  render() {
    return <div className="BalanceUserList">{this.renderBalanceUsers()}</div>;
  }
}

BalanceUserList.propTypes = {
  customerUserStore: modelOf(CustomerUserStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  history: RouterPropTypes.history.isRequired,
  intl: intlShape.isRequired,
  uiStore: modelOf(UIStore).isRequired,
  showBalance: PropTypes.bool,
  onBalanceUpdate: PropTypes.func,
  onRemoveUser: PropTypes.func,
  totalSums: PropTypes.array,
  onBalanceError: PropTypes.func
};

export default inject('routeService', 'uiStore', 'customerUserStore')(
  withRouter(injectIntl(BalanceUserList))
);



// WEBPACK FOOTER //
// ./src/components/my-account/BalanceUser/BalanceUserList/index.jsx